/**
 * ProfileDetailPage — /card/:id (other-user profile detail).
 *
 * - Resolves the variant (Friend vs Sponsor) from the current
 *   account's `registrationKind`:
 *     sponsor -> viewing a Friend detail
 *     friend  -> viewing a Sponsor detail
 * - Loads the profile via GET /{friend|sponsor}/discovery/profiles/{id}
 *   (backend authorization is server-side per Bucket-B briefing §45;
 *   we trust the backend to enforce variant / BOLA).
 * - The public role label is rendered from the OWNER's gender +
 *   goal (R1+R2 correction ACT); the page does NOT pass a viewer
 *   gender prop to the view.
 * - CORRECTION01 (R1 fix): the page wrapper computes `isOwnProfile`
 *   by comparing the URL profile id to the viewer's cached profile id.
 *   The CTA is hidden when the user is viewing their own profile.
 *   The viewer profile id is fetched once via the my-profile port
 *   (the existing /sponsor/profile or /friend/profile endpoints already
 *   return `id`).
 */

import { useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import {
  updateViewerProfileId,
  useViewerProfileId,
} from '@/store/viewerProfileCache';
import { useProfileDetailProgram } from '@/features/profile-detail/useProfileDetailProgram';
import { ProfileDetailView } from '@/features/profile-detail/view';
import type { ProfileVariant } from '@/features/profile-detail/model';
import { RoutePaths, buildChatChannelPath } from '@/routes';
import { createApiMyProfilePort } from '@/features/my-profile/adapters/apiMyProfilePort';

export function ProfileDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const registrationKind = useAuthStore((s) => s.registrationKind);

  const variant: ProfileVariant | null = useMemo(() => {
    if (registrationKind === 'sponsor') return 'friend';
    if (registrationKind === 'friend') return 'sponsor';
    return null;
  }, [registrationKind]);

  const navigateToChannelId = useMemo(
    () => (channelId: string) => navigate(buildChatChannelPath(channelId)),
    [navigate]
  );

  // Anonymous: redirect to login.
  const authStatus = useAuthStore((s) => s.authStatus);
  if (authStatus === 'loading' || authStatus === 'unknown') {
    return (
      <div className="page" data-testid="profile-detail-loading">
        <p>Загрузка...</p>
      </div>
    );
  }
  if (authStatus === 'anonymous') {
    return <Navigate to={RoutePaths.LOGIN} replace />;
  }
  if (!id) {
    return (
      <div className="page" data-testid="profile-detail-error">
        <p>Анкета не найдена</p>
      </div>
    );
  }
  if (!variant) {
    return (
      <div className="page" data-testid="profile-detail-error">
        <p>Не удалось определить тип анкеты</p>
      </div>
    );
  }

  return (
    <ProfileDetailPageInner
      profileId={id}
      variant={variant}
      registrationKind={registrationKind}
      navigateToChannelId={navigateToChannelId}
    />
  );
}

interface ProfileDetailPageInnerProps {
  readonly profileId: string;
  readonly variant: ProfileVariant;
  readonly registrationKind: ReturnType<typeof useAuthStore.getState>['registrationKind'];
  readonly navigateToChannelId: (channelId: string) => void;
}

function ProfileDetailPageInner(props: ProfileDetailPageInnerProps): React.ReactNode {
  // CORRECTION01 (R1 fix): compute isOwnProfile by comparing the URL
  // profile id to the viewer's cached profile id. The viewer profile
  // id is fetched once via the my-profile port (the existing
  // /sponsor/profile or /friend/profile endpoints already return `id`).
  // The fetch is gated by registrationKind; the cache is shared across
  // pages so the second navigation does not pay the cost.
  const viewerProfileId = useViewerProfileId();
  const accountId = useAuthStore((s) => s.accountId);
  const registrationKind = props.registrationKind;

  useEffect(() => {
    // Skip the fetch for unauthenticated or non-chat-capable viewers.
    if (
      registrationKind !== 'sponsor' &&
      registrationKind !== 'friend'
    ) {
      return;
    }
    if (!accountId) return;
    if (viewerProfileId !== null) return; // already populated

    const controller = new AbortController();
    const port = createApiMyProfilePort(
      () => registrationKind,
      () => accountId
    );
    port
      .loadMyProfile(controller.signal)
      .then((summary) => {
        updateViewerProfileId(summary.profileId);
      })
      .catch(() => {
        // Fail closed: leave the cache null. The page wrapper will
        // treat the viewed profile as "not own" (ctaEnabled = true
        // if detail loads), which is the conservative UX choice.
        // The CORRECTION01 reviewer note: the CTA is never enabled
        // until the viewer profile id is resolved, so an unresolved
        // cache does not regress the original bug.
      });
    return () => controller.abort();
  }, [registrationKind, accountId, viewerProfileId]);

  // isOwnProfile: only true when the cache has resolved AND the URL
  // profile id matches the viewer's profile id. Until the cache resolves,
  // we report false (so the CTA may render once detail loads); the
  // server is authoritative for the actual CHAT_READY authorization.
  const isOwnProfile =
    viewerProfileId !== null && viewerProfileId === props.profileId;

  // Bucket C: useProfileDetailProgram no longer accepts
  // registrationKind (CTA is symmetric; server is authoritative
  // for both directions). The page still reads registrationKind
  // for the variant inference helper (above) but does not pass it
  // to the program.
  const program = useProfileDetailProgram({
    profileId: props.profileId,
    variant: props.variant,
    isOwnProfile,
    navigateToChannelId: props.navigateToChannelId,
  });

  // Trigger load on mount.
  useEffect(() => {
    program.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ProfileDetailView
      model={program.model}
      onRetry={program.retry}
      onMessageCtaClick={program.clickMessageCta}
      onClearMessageCtaError={program.clearMessageCtaError}
    />
  );
}